/**
 * Utilitaires pour la gestion des erreurs API
 * Extrait les messages d'erreur du backend pour les afficher aux utilisateurs
 */

import { AxiosError } from 'axios';

// ========================================
// Types
// ========================================

interface ApiErrorResponse {
    success?: false;
    message?: string;
    error?: string;
    errors?: Record<string, string[]>;
    statusCode?: number;
}

export interface ParsedError {
    title: string;
    message: string;
    statusCode?: number;
    fieldErrors?: Record<string, string[]>;
}

// ========================================
// Error Parser
// ========================================

/**
 * Extrait un message d'erreur lisible d'une erreur Axios
 */
export function parseApiError(error: unknown): ParsedError {
    // Erreur Axios
    if (error instanceof AxiosError) {
        const response = error.response;
        const data = response?.data as ApiErrorResponse | undefined;
        const status = response?.status;

        // Erreur réseau
        if (!response) {
            return {
                title: 'Erreur réseau',
                message: 'Impossible de contacter le serveur. Vérifiez votre connexion internet ou que le serveur est démarré.',
            };
        }

        // Message d'erreur du backend
        const backendMessage = data?.message || data?.error;

        // Erreurs de validation (champs)
        if (data?.errors && Object.keys(data.errors).length > 0) {
            const fieldMessages = Object.entries(data.errors)
                .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                .join('\n');

            return {
                title: 'Erreur de validation',
                message: fieldMessages,
                statusCode: status,
                fieldErrors: data.errors,
            };
        }

        // Codes d'erreur spécifiques
        switch (status) {
            case 400:
                return {
                    title: 'Requête invalide',
                    message: backendMessage || 'Les données envoyées sont incorrectes.',
                    statusCode: status,
                };

            case 401:
                return {
                    title: 'Non authentifié',
                    message: backendMessage || 'Votre session a expiré. Veuillez vous reconnecter.',
                    statusCode: status,
                };

            case 403:
                return {
                    title: 'Accès refusé',
                    message: backendMessage || "Vous n'avez pas les droits pour effectuer cette action.",
                    statusCode: status,
                };

            case 404:
                return {
                    title: 'Ressource introuvable',
                    message: backendMessage || "L'élément demandé n'existe pas ou a été supprimé.",
                    statusCode: status,
                };

            case 409:
                return {
                    title: 'Conflit',
                    message: backendMessage || 'Cette opération entre en conflit avec des données existantes.',
                    statusCode: status,
                };

            case 422:
                return {
                    title: 'Données invalides',
                    message: backendMessage || 'Les données fournies ne sont pas valides.',
                    statusCode: status,
                };

            case 429:
                return {
                    title: 'Trop de requêtes',
                    message: 'Veuillez patienter avant de réessayer.',
                    statusCode: status,
                };

            case 500:
            case 502:
            case 503:
            case 504:
                return {
                    title: 'Erreur serveur',
                    message: backendMessage || 'Une erreur est survenue côté serveur. Veuillez réessayer plus tard.',
                    statusCode: status,
                };

            default:
                return {
                    title: `Erreur ${status || 'inconnue'}`,
                    message: backendMessage || error.message || 'Une erreur inattendue est survenue.',
                    statusCode: status,
                };
        }
    }

    // Erreur JavaScript standard
    if (error instanceof Error) {
        return {
            title: 'Erreur',
            message: error.message,
        };
    }

    // Erreur inconnue
    return {
        title: 'Erreur inconnue',
        message: String(error),
    };
}

/**
 * Retourne juste le message d'erreur (version courte)
 */
export function getErrorMessage(error: unknown): string {
    const parsed = parseApiError(error);
    return parsed.message;
}

/**
 * Log l'erreur en console avec détails (pour debug)
 */
export function logError(context: string, error: unknown): void {
    const parsed = parseApiError(error);
    console.error(`[${context}] ${parsed.title}:`, parsed.message);
    if (import.meta.env.DEV) {
        console.error('Full error:', error);
    }
}
